import { Button } from '@citizen-walk/shared-ui';
import type {
  AiCategoryMaster,
  AiGenerationConfig,
  AiModelGeneration,
  AiOutputFormat,
  AiPromptInputMapping,
  AiTitleGenerationConfig,
  AiValidatorCountMode,
  AiValidatorOrderBy,
  AiValidatorRule,
  AiValidatorRuleKind,
  AiValidatorsConfig,
  DistrictMode,
} from '@citizen-walk/shared-types';
import {
  AI_MODEL_GENERATIONS,
  AI_MODEL_GENERATION_LABELS,
  aiGenerationSupportsSampling,
  aiGenerationSupportsThinkingBudget,
  aiGenerationSupportsThinkingLevel,
  applyAiModelGeneration,
  normalizeAiOutputFormat,
  resolveAiModelGeneration,
} from '@citizen-walk/shared-types';
import { useState } from 'react';

/**
 * AI 解析設定 (M1) のサブフォーム共通モジュール。
 *
 * 元は `AiAnalysisSettingsPage.tsx` に module-private で定義されていた
 * GenerationConfigForm / TitleGenerationForm / ValidatorRulesForm (+ ValidatorRuleRow) /
 * PromptInputMappingForm と純関数 validateRules / buildFieldPathOptions を、AI 解析統合エディタ
 * (AiAnalysisEditorPage) からも再利用するため本ファイルへ切り出した。M1 ページと統合エディタが
 * 同じサブフォーム部品を共有し、二重実装を回避する (CLAUDE.md「再利用」)。
 *
 * 各フォームは完全 controlled (内部 state は ValidatorRuleRow の表示都合のみ)。
 */

export const FIELD_CLASS =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400';
export const INPUT_CLASS =
  'w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400';

/** kind ごとの日本語表示ラベル (画面上で「日本語 (key)」形式で表示するため)。 */
export const RULE_KIND_LABELS: Record<AiValidatorRuleKind, string> = {
  allowedValues: 'ホワイトリスト',
  maxItems: '配列要素数上限',
  lengthRange: '文字列長範囲',
  categorySelection: 'カテゴリマスタからの選定',
};

/**
 * fieldPath dropdown 候補 — mode ごとの well-known な出力フィールド名。
 * M3 outputFormatSchema.classificationAxes[].codeField があれば dynamic に追加される。
 */
/**
 * @deprecated 【廃止予定 fallback】mode 別の静的 fieldPath 候補 (モードドリブン化 2026-07-24)。
 * 候補の正は選択中の M3 (`buildFieldPathOptions` が `normalizeAiOutputFormat` から導出) で、
 * 本マップは M3 未選択時 / legacy 地区で候補が空にならないための補完にすぎない。
 * 撤去条件: legacy 地区 0 件 (issue #45)。新規コードから参照しないこと。
 */
export const MODE_FIELD_PATH_SUGGESTIONS: Record<DistrictMode, string[]> = {
  ecw: [
    'emotions',
    'category_codes',
    'description',
    'emotion_text',
    'emotion_reason',
    'category_reason',
  ],
  bousai: ['classifications', 'description', 'professional_insight', 'reasoning'],
  walker: ['classifications', 'description', 'professional_insight'],
  'kids-bousai': ['classifications', 'description', 'professional_insight', 'reasoning'],
};

/** fieldPath dropdown の追加項目: 「カスタム入力」(value=__custom__) を選ぶと自由入力 input に切替。 */
export const FIELD_PATH_CUSTOM_VALUE = '__custom__';

/** モデル名 dropdown の追加項目: 「その他」(value=__custom__) を選ぶと自由入力 input に切替。 */
export const MODEL_CUSTOM_VALUE = '__custom__';

/** analyzePost のランタイム既定モデル (generationConfig.model 未設定時に使われる)。 */
export const DEFAULT_MODEL_NAME = 'gemini-3.5-flash-lite';

/**
 * 世代ごとのモデル名 dropdown 候補 — 実投稿写真 + 本番同一プロンプトでの実測値に基づく推奨順。
 * 世代を先に選ぶとこの候補だけに絞り込まれる (世代とモデルの不整合な組合せを作らせない)。
 * ここに無いモデルはどちらの世代でも「その他 (モデル名を直接入力)」で自由入力できる。
 */
export const MODEL_OPTIONS_BY_GENERATION: Record<
  AiModelGeneration,
  { value: string; label: string }[]
> = {
  v3: [
    { value: 'gemini-3.5-flash-lite', label: 'gemini-3.5-flash-lite (高速・低コスト / 推奨)' },
    { value: 'gemini-3.7-flash', label: 'gemini-3.7-flash (高品質・低速)' },
  ],
  v2: [{ value: 'gemini-2.5-flash', label: 'gemini-2.5-flash (従来)' }],
};

/** 全世代のモデル候補を並べたもの (一覧表示・保存済モデルの判定用)。 */
export const MODEL_OPTIONS: { value: string; label: string }[] = [
  ...MODEL_OPTIONS_BY_GENERATION.v3,
  ...MODEL_OPTIONS_BY_GENERATION.v2,
];

/** 世代を切替えたときに選ばれるモデル (不整合な組合せを残さないための既定)。 */
export const DEFAULT_MODEL_BY_GENERATION: Record<AiModelGeneration, string> = {
  v3: DEFAULT_MODEL_NAME,
  v2: 'gemini-2.5-flash',
};

/**
 * 「実際に使われるモデル名」を求める pure 関数。
 * `generationConfig.model` は未設定を許すフィールドで、未設定ならランタイム既定
 * (DEFAULT_MODEL_NAME) が使われる。一覧・dropdown で「どのテンプレが旧モデルのままか」を
 * 見分けられるよう、未設定でも空欄にせず既定モデル名 + 既定フラグを返す。
 */
export function effectiveModelName(generationConfig?: { model?: string } | null): {
  model: string;
  isDefault: boolean;
} {
  const model = generationConfig?.model;
  return { model: model || DEFAULT_MODEL_NAME, isDefault: !model };
}

/** thinkingLevel dropdown の候補 (先頭は「未指定」= モデル既定)。 */
export const THINKING_LEVEL_OPTIONS: NonNullable<
  NonNullable<AiGenerationConfig['thinkingConfig']>['thinkingLevel']
>[] = ['minimal', 'low', 'medium', 'high'];

/**
 * fieldPath dropdown の候補を構築する。
 *
 * **正は選択中の M3 (出力フォーマット)** — `normalizeAiOutputFormat` の `fields[]` が出力列名の
 * 唯一の真実 (docs/105 レベル 3) なので、そこから導出する。mode 文字列は挙動を決めないため
 * (モードドリブン化 2026-07-24)、custom mode でも M3 さえあれば正しい候補が出る。
 *
 * 並び順は「M3 由来 → 集約スコア/理由 → 軸 → legacy の mode 別候補」。最後の 1 群は
 * M3 未選択や legacy 地区で空にならないようにするための fallback (撤去条件: issue #45)。
 */
export function buildFieldPathOptions(
  mode: DistrictMode,
  outputFormat: (AiOutputFormat & { id: string }) | undefined,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (name: string | undefined) => {
    const v = name?.trim();
    if (!v || seen.has(v)) return;
    seen.add(v);
    out.push(v);
  };
  const schema = outputFormat?.schema ? normalizeAiOutputFormat(outputFormat.schema) : undefined;
  for (const f of schema?.fields ?? []) {
    push(f.key);
    if (f.kind === 'object-array') {
      for (const item of f.itemFields ?? []) push(`${f.key}.${item.key}`);
    }
  }
  push(schema?.aggregateScores?.positiveKey);
  push(schema?.aggregateScores?.negativeKey);
  push(schema?.aggregateScores?.overallKey);
  push(schema?.reasoningField);
  for (const ax of schema?.classificationAxes ?? []) {
    push(ax.codeField);
    push(ax.nameField);
    push(ax.subField);
  }
  // 【廃止予定 fallback】mode 別の静的候補 (撤去条件: legacy 地区 0 件、issue #45)。
  for (const f of MODE_FIELD_PATH_SUGGESTIONS[mode] ?? []) push(f);
  return out;
}

// ---------------------------------------------------------------------------
// 1. GenerationConfigForm — Gemini generationConfig (M1.generationConfig)
// ---------------------------------------------------------------------------

export function GenerationConfigForm(props: {
  value: AiGenerationConfig;
  onChange: (next: AiGenerationConfig) => void;
}) {
  const { value, onChange } = props;
  // 世代は「保存された generation」を最優先、未設定の既存データはモデル名から推定する。
  const generation = resolveAiModelGeneration(value);
  const modelOptions = MODEL_OPTIONS_BY_GENERATION[generation];
  // 保存済みのモデル名がこの世代の候補に無ければ自由入力モード扱い (他モデル指定済みの地区など)。
  const isCustomModel = !!value.model && !modelOptions.some((opt) => opt.value === value.model);
  const [customModelModeState, setCustomModelMode] = useState(isCustomModel);
  const customModelMode = customModelModeState || isCustomModel;

  /**
   * 世代を切り替える。モデルもその世代の既定へ揃え、その世代で使わない値
   * (第3世代なら温度 / Top-P / Top-K / 思考トークン予算、第2世代なら推論レベル) は
   * 保存データから削除する。最大出力トークン数と応答 MIME 型は世代共通なので保持する。
   */
  function setGeneration(next: AiModelGeneration) {
    setCustomModelMode(false);
    onChange(applyAiModelGeneration({ ...value, model: DEFAULT_MODEL_BY_GENERATION[next] }, next));
  }

  /** model を書き換える (空文字ならキー自体を削除してランタイム既定に戻す)。 */
  function setModel(v: string) {
    const next: AiGenerationConfig = { ...value };
    if (v.trim()) {
      next.model = v;
    } else {
      delete next.model;
    }
    onChange(next);
  }

  /** thinkingLevel を書き換える (未指定なら thinkingConfig ごと削除して空オブジェクトを残さない)。 */
  function setThinkingLevel(v: string) {
    const next: AiGenerationConfig = { ...value };
    if (v) {
      next.thinkingConfig = {
        ...(next.thinkingConfig ?? {}),
        thinkingLevel: v as NonNullable<AiGenerationConfig['thinkingConfig']>['thinkingLevel'],
      };
    } else if (next.thinkingConfig) {
      const { thinkingLevel: _drop, ...rest } = next.thinkingConfig;
      if (Object.keys(rest).length > 0) {
        next.thinkingConfig = rest;
      } else {
        delete next.thinkingConfig;
      }
    }
    onChange(next);
  }

  /** thinkingBudget を書き換える (空欄なら thinkingConfig ごと削除、0 は有効値として保持)。 */
  function setThinkingBudget(v: string) {
    const next: AiGenerationConfig = { ...value };
    if (v !== '' && Number.isFinite(Number(v))) {
      next.thinkingConfig = { ...(next.thinkingConfig ?? {}), thinkingBudget: Number(v) };
    } else if (next.thinkingConfig) {
      const { thinkingBudget: _drop, ...rest } = next.thinkingConfig;
      if (Object.keys(rest).length > 0) {
        next.thinkingConfig = rest;
      } else {
        delete next.thinkingConfig;
      }
    }
    onChange(next);
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="md:col-span-2">
        <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-generation">
          Gemini のバージョン <span className="text-danger-600">*</span>
        </label>
        <select
          id="m1-gen-generation"
          value={generation}
          onChange={(e) => setGeneration(e.target.value as AiModelGeneration)}
          className={`${FIELD_CLASS} bg-white`}
          aria-label="Gemini のバージョン"
        >
          {AI_MODEL_GENERATIONS.map((g) => (
            <option key={g} value={g}>
              {AI_MODEL_GENERATION_LABELS[g]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
          バージョン (世代) によって使える項目が違うため、まず最初にバージョンを選びます。以下の
          モデル候補と入力欄は、選んだ世代で実際に効くものだけに切り替わります。世代を変えるとモデルも
          その世代の既定に切り替わり、その世代で使わない値は保存されません。
          <br />
          「その他 (モデル名を直接入力)」で候補に無いモデルを指定した場合は、ここで選んだ世代がその
          モデルの扱い (AI に送る項目) を決めます。
        </p>
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-model">
          モデル名 (model、省略時は {DEFAULT_MODEL_NAME})
        </label>
        {!customModelMode ? (
          <select
            id="m1-gen-model"
            value={value.model ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === MODEL_CUSTOM_VALUE) {
                setCustomModelMode(true);
                setModel('');
                return;
              }
              setModel(v);
            }}
            className={`${FIELD_CLASS} bg-white`}
            aria-label="モデル名 (model)"
          >
            <option value="">既定 ({DEFAULT_MODEL_NAME})</option>
            {modelOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
            <option value={MODEL_CUSTOM_VALUE}>その他 (モデル名を直接入力)</option>
          </select>
        ) : (
          <div className="flex gap-2">
            <input
              id="m1-gen-model"
              type="text"
              value={value.model ?? ''}
              onChange={(e) => setModel(e.target.value)}
              className={`${FIELD_CLASS} flex-1`}
              placeholder={DEFAULT_MODEL_BY_GENERATION[generation]}
              aria-label="モデル名 (model)"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setCustomModelMode(false);
                setModel('');
              }}
              aria-label="モデル名を dropdown 選択に戻す"
            >
              候補から選ぶ
            </Button>
          </div>
        )}
        <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
          実測 (1 件あたり): gemini-3.5-flash-lite は約 3 秒 / 約 $0.0045、gemini-3.7-flash は約 18
          秒 / 約 $0.017 で記述が最も精緻、gemini-2.5-flash (従来) は約 17.5 秒 / 約 $0.0090。
        </p>
        {generation === 'v2' && !value.model && (
          <p className="mt-1 text-[11px] text-danger-600 leading-relaxed">
            モデル名が未指定のため、実行時は既定の {DEFAULT_MODEL_NAME} (第3世代) が使われます。
            第2世代の設定を効かせるにはモデル名を指定してください。
          </p>
        )}
      </div>
      {aiGenerationSupportsThinkingLevel(generation) && (
        <div>
          <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-thinkingLevel">
            推論レベル (thinkingLevel)
          </label>
          <select
            id="m1-gen-thinkingLevel"
            value={value.thinkingConfig?.thinkingLevel ?? ''}
            onChange={(e) => setThinkingLevel(e.target.value)}
            className={`${FIELD_CLASS} bg-white`}
            aria-label="推論レベル (thinkingLevel)"
          >
            <option value="">未指定 (モデル既定)</option>
            {THINKING_LEVEL_OPTIONS.map((lv) => (
              <option key={lv} value={lv}>
                {lv}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
            上げるほど記述が充実しますが、速度とコストが増えます (実測: gemini-3.5-flash-lite で low
            約 3 秒 → high 約 8 秒、コストは約 2 倍)。
            <br />
            <span className="text-danger-600">
              gemini-3.7-flash は minimal に対応していないため、自動的に low として実行されます。
            </span>
          </p>
        </div>
      )}
      {aiGenerationSupportsThinkingBudget(generation) && (
        <div>
          <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-thinkingBudget">
            思考トークン予算 (thinkingBudget)
          </label>
          <input
            id="m1-gen-thinkingBudget"
            type="number"
            step={1}
            min={0}
            value={
              typeof value.thinkingConfig?.thinkingBudget === 'number'
                ? value.thinkingConfig.thinkingBudget
                : ''
            }
            onChange={(e) => setThinkingBudget(e.target.value)}
            className={FIELD_CLASS}
            placeholder="未指定 (モデル既定)"
            aria-label="思考トークン予算 (thinkingBudget)"
          />
          <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
            空欄ならモデル既定。0
            を指定すると内部推論を使わず、最大出力トークン数を本文だけに使えます
            (内部推論に予算を食われて本文が途中で切れる場合の対処)。
          </p>
        </div>
      )}
      {aiGenerationSupportsSampling(generation) ? (
        <>
          <div>
            <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-temperature">
              温度 (temperature) <span className="text-danger-600">*</span>
            </label>
            <input
              id="m1-gen-temperature"
              type="number"
              step={0.1}
              value={Number.isFinite(value.temperature) ? value.temperature : ''}
              onChange={(e) =>
                onChange({
                  ...value,
                  temperature: e.target.value === '' ? NaN : Number(e.target.value),
                })
              }
              className={FIELD_CLASS}
            />
            <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
              出力のばらつき。実測: gemini-2.5-flash に同じプロンプトを 6 回投げると、0.0
              ではほぼ同じ文面に、2.0 では毎回違う文面になりました。
            </p>
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-topP">
              Top-P 値 (topP) <span className="text-danger-600">*</span>
            </label>
            <input
              id="m1-gen-topP"
              type="number"
              step={0.1}
              value={Number.isFinite(value.topP) ? value.topP : ''}
              onChange={(e) =>
                onChange({ ...value, topP: e.target.value === '' ? NaN : Number(e.target.value) })
              }
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-topK">
              Top-K 値 (topK) <span className="text-danger-600">*</span>
            </label>
            <input
              id="m1-gen-topK"
              type="number"
              step={1}
              value={Number.isFinite(value.topK) ? value.topK : ''}
              onChange={(e) =>
                onChange({ ...value, topK: e.target.value === '' ? NaN : Number(e.target.value) })
              }
              className={FIELD_CLASS}
            />
          </div>
        </>
      ) : (
        <div className="md:col-span-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 leading-relaxed">
          第3世代では 温度 (temperature) / Top-P 値 (topP) / Top-K 値 (topK) が API に無視されるため
          (実測: 0.0 でも 2.0 でも出力のばらつきは変わりませんでした)、この画面では扱いません。値も
          保存されず、AI にも送られません。
        </div>
      )}
      <div>
        <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-maxOutputTokens">
          最大出力トークン数 (maxOutputTokens) <span className="text-danger-600">*</span>
        </label>
        <input
          id="m1-gen-maxOutputTokens"
          type="number"
          step={128}
          value={Number.isFinite(value.maxOutputTokens) ? value.maxOutputTokens : ''}
          onChange={(e) =>
            onChange({
              ...value,
              maxOutputTokens: e.target.value === '' ? NaN : Number(e.target.value),
            })
          }
          className={FIELD_CLASS}
        />
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-gen-responseMimeType">
          応答 MIME 型 (responseMimeType) <span className="text-danger-600">*</span>
        </label>
        <select
          id="m1-gen-responseMimeType"
          value={value.responseMimeType || 'application/json'}
          onChange={(e) => onChange({ ...value, responseMimeType: e.target.value })}
          className={`${FIELD_CLASS} bg-white`}
        >
          <option value="application/json">application/json</option>
          <option value="text/plain">text/plain</option>
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. TitleGenerationForm — タイトル自動生成 (M1.titleGeneration、optional)
// ---------------------------------------------------------------------------

export function TitleGenerationForm(props: {
  value: AiTitleGenerationConfig | null;
  onChange: (next: AiTitleGenerationConfig | null) => void;
}) {
  const { value, onChange } = props;
  const present = value !== null;
  return (
    <div className="grid grid-cols-1 gap-2">
      <label className="inline-flex items-center gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={present}
          onChange={(e) => {
            if (e.target.checked) {
              onChange({ enabled: false, instructions: '' });
            } else {
              onChange(null);
            }
          }}
          aria-label="タイトル自動生成設定を有効化 (titleGeneration)"
        />
        <span>タイトル自動生成設定を有効化 (titleGeneration、未設定なら省略)</span>
      </label>
      {present && (
        <>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <input
              id="m1-title-enabled"
              type="checkbox"
              checked={value.enabled}
              onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
            />
            <span>タイトル自動生成を有効化 (enabled)</span>
          </label>
          <div>
            <label className="block text-xs text-slate-600 mb-1" htmlFor="m1-title-instructions">
              タイトル生成プロンプト (instructions)
            </label>
            <textarea
              id="m1-title-instructions"
              value={value.instructions}
              onChange={(e) => onChange({ ...value, instructions: e.target.value })}
              rows={3}
              disabled={!value.enabled}
              className={`${FIELD_CLASS} resize-y ${value.enabled ? '' : 'bg-slate-100 text-slate-400'}`}
              placeholder="例: 写真の景観解析結果と投稿者の入力をふまえ、投稿のサブタイトルを1文（20〜40字）で生成してください。"
            />
            <p className="mt-1 text-[10px] text-slate-400">
              投稿の入力データ（写真以外）と解析結果（description
              等）は他のAI解析と同様に自動で渡されます。 指示文の中で {'{{title}}'}{' '}
              のような入力項目の指定を書く必要はありません。
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. ValidatorRulesForm — 出力検証ルール (M1.validators.rules、R-38 で mode 非依存化)
// ---------------------------------------------------------------------------

/**
 * 単一ルール行のエディタ。fieldPath / kind / kind 別の値編集 UI を 1 行で扱う。
 * 行全体を controlled で受け取り、変更は onChange(next) で親に通知する。
 *
 * fieldPath は dropdown 形式: mode + M3 outputFormat から候補を導出 (buildFieldPathOptions)。
 * 候補にない値は「カスタム入力」で自由入力可能。
 */
export function ValidatorRuleRow(props: {
  index: number;
  value: AiValidatorRule;
  onChange: (next: AiValidatorRule) => void;
  onRemove: () => void;
  fieldPathOptions: string[];
  /** 当該 M1 で参照する M2 (categoryMasterIds) の displayName lookup 用。 */
  availableCategoryMasters: (AiCategoryMaster & { id: string })[];
}) {
  const { index, value, onChange, onRemove, fieldPathOptions, availableCategoryMasters } = props;
  const [allowedInput, setAllowedInput] = useState('');
  // fieldPath が候補に無ければカスタム入力モード扱い
  const isCustomFieldPath =
    value.fieldPath.length > 0 && !fieldPathOptions.includes(value.fieldPath);
  const [customFieldPathMode, setCustomFieldPathMode] = useState(isCustomFieldPath);

  function setKind(nextKind: AiValidatorRuleKind) {
    // kind 切替時は他 kind 用の値をクリアして整合性を保つ。
    // categorySelection は default 値を seed しておくと UI 操作が楽。
    if (nextKind === 'categorySelection') {
      onChange({
        fieldPath: value.fieldPath,
        kind: 'categorySelection',
        categoryMasterId: availableCategoryMasters[0]?.id ?? '',
        selectionCount: 1,
        selectionCountMode: 'max',
        selectionOrderBy: 'rank',
        requireReason: false,
      });
      return;
    }
    onChange({ fieldPath: value.fieldPath, kind: nextKind });
  }

  function addAllowedValue() {
    const tag = allowedInput.trim();
    if (!tag) return;
    const cur = value.allowedValues ?? [];
    if (cur.includes(tag)) {
      setAllowedInput('');
      return;
    }
    onChange({ ...value, allowedValues: [...cur, tag] });
    setAllowedInput('');
  }

  function removeAllowedValue(i: number) {
    const cur = value.allowedValues ?? [];
    onChange({ ...value, allowedValues: cur.filter((_, idx) => idx !== i) });
  }

  return (
    <li className="border border-slate-200 rounded-md p-3 bg-slate-50">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-slate-700">ルール {index + 1}</span>
        <Button
          variant="danger"
          size="sm"
          onClick={onRemove}
          aria-label={`ルール ${index + 1} 削除`}
        >
          ✕ 削除
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div>
          <label
            className="block text-xs text-slate-600 mb-1"
            htmlFor={`m1-rule-${index}-fieldPath`}
          >
            フィールドパス (fieldPath) <span className="text-danger-600">*</span>
          </label>
          {!customFieldPathMode ? (
            <select
              id={`m1-rule-${index}-fieldPath`}
              value={value.fieldPath}
              onChange={(e) => {
                const v = e.target.value;
                if (v === FIELD_PATH_CUSTOM_VALUE) {
                  setCustomFieldPathMode(true);
                  onChange({ ...value, fieldPath: '' });
                  return;
                }
                onChange({ ...value, fieldPath: v });
              }}
              className={`${INPUT_CLASS} bg-white`}
              aria-label={`ルール ${index + 1} フィールドパス (fieldPath)`}
            >
              <option value="">(選択してください)</option>
              {fieldPathOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
              <option value={FIELD_PATH_CUSTOM_VALUE}>カスタム入力…</option>
            </select>
          ) : (
            <div className="flex gap-2">
              <input
                id={`m1-rule-${index}-fieldPath`}
                type="text"
                value={value.fieldPath}
                onChange={(e) => onChange({ ...value, fieldPath: e.target.value })}
                className={`${INPUT_CLASS} flex-1`}
                placeholder="例: extraFields.foo"
                aria-label={`ルール ${index + 1} フィールドパス (fieldPath)`}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setCustomFieldPathMode(false);
                  onChange({ ...value, fieldPath: '' });
                }}
                aria-label={`ルール ${index + 1} フィールドパスを dropdown 選択に戻す`}
              >
                候補から選ぶ
              </Button>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-1" htmlFor={`m1-rule-${index}-kind`}>
            種別 (kind) <span className="text-danger-600">*</span>
          </label>
          <select
            id={`m1-rule-${index}-kind`}
            value={value.kind}
            onChange={(e) => setKind(e.target.value as AiValidatorRuleKind)}
            className={`${INPUT_CLASS} bg-white`}
            aria-label={`ルール ${index + 1} 種別 (kind)`}
          >
            <option value="allowedValues">{RULE_KIND_LABELS.allowedValues} (allowedValues)</option>
            <option value="maxItems">{RULE_KIND_LABELS.maxItems} (maxItems)</option>
            <option value="lengthRange">{RULE_KIND_LABELS.lengthRange} (lengthRange)</option>
            <option value="categorySelection">
              {RULE_KIND_LABELS.categorySelection} (categorySelection)
            </option>
          </select>
        </div>
      </div>

      {value.kind === 'allowedValues' && (
        <div className="mt-2">
          <label
            className="block text-xs text-slate-600 mb-1"
            htmlFor={`m1-rule-${index}-allowedValues-input`}
          >
            許可値 (allowedValues、{value.allowedValues?.length ?? 0} 件)
          </label>
          <div className="flex gap-2 mb-2">
            <input
              id={`m1-rule-${index}-allowedValues-input`}
              type="text"
              value={allowedInput}
              onChange={(e) => setAllowedInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addAllowedValue();
                }
              }}
              className={`${INPUT_CLASS} flex-1`}
              placeholder="例: 喜び"
              aria-label={`ルール ${index + 1} 許可値入力 (allowedValues)`}
            />
            <Button variant="secondary" size="sm" onClick={addAllowedValue}>
              ＋ 追加
            </Button>
          </div>
          {(value.allowedValues?.length ?? 0) > 0 && (
            <ul
              className="flex flex-wrap gap-1"
              aria-label={`ルール ${index + 1} 許可値一覧 (allowedValues)`}
            >
              {value.allowedValues!.map((tag, i) => (
                <li
                  key={`${tag}-${i}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white border border-slate-200 rounded-full"
                >
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => removeAllowedValue(i)}
                    className="text-slate-400 hover:text-danger-600"
                    aria-label={`ルール ${index + 1} 許可値「${tag}」を削除`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {value.kind === 'maxItems' && (
        <div className="mt-2">
          <label
            className="block text-xs text-slate-600 mb-1"
            htmlFor={`m1-rule-${index}-maxItems`}
          >
            要素数上限 (maxItems) <span className="text-danger-600">*</span>
          </label>
          <input
            id={`m1-rule-${index}-maxItems`}
            type="number"
            step={1}
            min={0}
            value={typeof value.maxItems === 'number' ? value.maxItems : ''}
            onChange={(e) =>
              onChange({
                ...value,
                maxItems: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
            className={INPUT_CLASS}
            placeholder="例: 3"
            aria-label={`ルール ${index + 1} 要素数上限 (maxItems)`}
          />
        </div>
      )}

      {value.kind === 'lengthRange' && (
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              文字列長 最小 (lengthRange[0]) <span className="text-danger-600">*</span>
            </label>
            <input
              type="number"
              step={1}
              min={0}
              value={value.lengthRange ? value.lengthRange[0] : ''}
              onChange={(e) => {
                const min = e.target.value === '' ? NaN : Number(e.target.value);
                const max = value.lengthRange ? value.lengthRange[1] : NaN;
                onChange({
                  ...value,
                  lengthRange:
                    Number.isFinite(min) || Number.isFinite(max) ? [min, max] : undefined,
                });
              }}
              className={INPUT_CLASS}
              placeholder="min"
              aria-label={`ルール ${index + 1} 文字列長 最小 (lengthRange min)`}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              文字列長 最大 (lengthRange[1]) <span className="text-danger-600">*</span>
            </label>
            <input
              type="number"
              step={1}
              min={0}
              value={value.lengthRange ? value.lengthRange[1] : ''}
              onChange={(e) => {
                const min = value.lengthRange ? value.lengthRange[0] : NaN;
                const max = e.target.value === '' ? NaN : Number(e.target.value);
                onChange({
                  ...value,
                  lengthRange:
                    Number.isFinite(min) || Number.isFinite(max) ? [min, max] : undefined,
                });
              }}
              className={INPUT_CLASS}
              placeholder="max"
              aria-label={`ルール ${index + 1} 文字列長 最大 (lengthRange max)`}
            />
          </div>
        </div>
      )}

      {value.kind === 'categorySelection' && (
        <div className="mt-2 grid grid-cols-1 gap-2">
          <div>
            <label
              className="block text-xs text-slate-600 mb-1"
              htmlFor={`m1-rule-${index}-categoryMasterId`}
            >
              参照カテゴリマスタ (categoryMasterId) <span className="text-danger-600">*</span>
            </label>
            <select
              id={`m1-rule-${index}-categoryMasterId`}
              value={value.categoryMasterId ?? ''}
              onChange={(e) => onChange({ ...value, categoryMasterId: e.target.value })}
              className={`${INPUT_CLASS} bg-white`}
              aria-label={`ルール ${index + 1} 参照カテゴリマスタ (categoryMasterId)`}
            >
              <option value="">(選択してください)</option>
              {availableCategoryMasters.map((m2) => (
                <option key={m2.id} value={m2.id}>
                  {m2.displayName} ({m2.id})
                </option>
              ))}
            </select>
            {availableCategoryMasters.length === 0 && (
              <p className="text-[10px] text-slate-400 mt-1">
                ※ 当該 M1 の連動カテゴリマスタ ID (categoryMasterIds) を先に選択してください。
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div>
              <label
                className="block text-xs text-slate-600 mb-1"
                htmlFor={`m1-rule-${index}-selectionCount`}
              >
                選定件数 (selectionCount) <span className="text-danger-600">*</span>
              </label>
              <input
                id={`m1-rule-${index}-selectionCount`}
                type="number"
                step={1}
                min={1}
                value={typeof value.selectionCount === 'number' ? value.selectionCount : ''}
                onChange={(e) =>
                  onChange({
                    ...value,
                    selectionCount: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                className={INPUT_CLASS}
                placeholder="例: 3"
                aria-label={`ルール ${index + 1} 選定件数 (selectionCount)`}
              />
            </div>
            <div>
              <label
                className="block text-xs text-slate-600 mb-1"
                htmlFor={`m1-rule-${index}-selectionCountMode`}
              >
                件数モード (selectionCountMode) <span className="text-danger-600">*</span>
              </label>
              <select
                id={`m1-rule-${index}-selectionCountMode`}
                value={value.selectionCountMode ?? 'max'}
                onChange={(e) =>
                  onChange({
                    ...value,
                    selectionCountMode: e.target.value as AiValidatorCountMode,
                  })
                }
                className={`${INPUT_CLASS} bg-white`}
                aria-label={`ルール ${index + 1} 件数モード (selectionCountMode)`}
              >
                <option value="max">上限件数 (max)</option>
                <option value="exact">必須件数 (exact)</option>
              </select>
            </div>
          </div>
          <div>
            <label
              className="block text-xs text-slate-600 mb-1"
              htmlFor={`m1-rule-${index}-selectionOrderBy`}
            >
              並び順 (selectionOrderBy) <span className="text-danger-600">*</span>
            </label>
            <select
              id={`m1-rule-${index}-selectionOrderBy`}
              value={value.selectionOrderBy ?? 'rank'}
              onChange={(e) =>
                onChange({ ...value, selectionOrderBy: e.target.value as AiValidatorOrderBy })
              }
              className={`${INPUT_CLASS} bg-white`}
              aria-label={`ルール ${index + 1} 並び順 (selectionOrderBy)`}
            >
              <option value="rank">rank 昇順 (rank)</option>
              <option value="asListed">AI 返却順そのまま (asListed)</option>
            </select>
          </div>
          <div>
            <label className="inline-flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={value.requireReason === true}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange({ ...value, requireReason: true });
                  } else {
                    // 解除時は reasonFieldPath も削除
                    const next = { ...value, requireReason: false };
                    delete next.reasonFieldPath;
                    onChange(next);
                  }
                }}
                aria-label={`ルール ${index + 1} 選定理由を必須にする (requireReason)`}
              />
              <span>選定理由を必須にする (requireReason)</span>
            </label>
          </div>
          {value.requireReason && (
            <div>
              <label
                className="block text-xs text-slate-600 mb-1"
                htmlFor={`m1-rule-${index}-reasonFieldPath`}
              >
                選定理由フィールドパス (reasonFieldPath) <span className="text-danger-600">*</span>
              </label>
              <select
                id={`m1-rule-${index}-reasonFieldPath`}
                value={
                  value.reasonFieldPath && fieldPathOptions.includes(value.reasonFieldPath)
                    ? value.reasonFieldPath
                    : value.reasonFieldPath
                      ? FIELD_PATH_CUSTOM_VALUE
                      : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === FIELD_PATH_CUSTOM_VALUE) {
                    // カスタム入力モード: 空文字でいったん受けて自由入力に切替
                    onChange({ ...value, reasonFieldPath: '' });
                  } else {
                    onChange({ ...value, reasonFieldPath: v });
                  }
                }}
                className={`${INPUT_CLASS} bg-white`}
                aria-label={`ルール ${index + 1} 選定理由フィールドパス (reasonFieldPath)`}
              >
                <option value="">(選択してください)</option>
                {fieldPathOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
                <option value={FIELD_PATH_CUSTOM_VALUE}>カスタム入力…</option>
              </select>
              {/* カスタム入力時: 候補に無い値が選ばれていれば text input を補助表示 */}
              {value.reasonFieldPath !== undefined &&
                !fieldPathOptions.includes(value.reasonFieldPath) && (
                  <input
                    type="text"
                    value={value.reasonFieldPath}
                    onChange={(e) => onChange({ ...value, reasonFieldPath: e.target.value })}
                    className={`${INPUT_CLASS} mt-1`}
                    placeholder="例: category_reason"
                    aria-label={`ルール ${index + 1} 選定理由フィールドパス カスタム入力 (reasonFieldPath)`}
                  />
                )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function ValidatorRulesForm(props: {
  value: AiValidatorRule[];
  onChange: (next: AiValidatorRule[]) => void;
  mode: DistrictMode;
  outputFormat: (AiOutputFormat & { id: string }) | undefined;
  availableCategoryMasters: (AiCategoryMaster & { id: string })[];
}) {
  const { value, onChange, mode, outputFormat, availableCategoryMasters } = props;
  const fieldPathOptions = buildFieldPathOptions(mode, outputFormat);

  function addRule() {
    onChange([...value, { fieldPath: '', kind: 'allowedValues', allowedValues: [] }]);
  }

  function updateRule(i: number, next: AiValidatorRule) {
    const out = [...value];
    out[i] = next;
    onChange(out);
  }

  function removeRule(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          ※ 出力 JSON の任意フィールドパスに対して検証ルール (allowedValues / maxItems / lengthRange
          / categorySelection) を追加できます。mode に関係なく適用され、fieldPath が
          出力に存在しないルールは単に無視されます。
        </p>
        <Button variant="secondary" size="sm" onClick={addRule}>
          ＋ ルール追加
        </Button>
      </div>
      {value.length === 0 ? (
        <p className="text-xs text-slate-500">
          ルールがありません。「＋ ルール追加」で検証ルールを追加してください (任意)。
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-2" aria-label="検証ルール一覧 (validators.rules)">
          {value.map((rule, i) => (
            <ValidatorRuleRow
              key={i}
              index={i}
              value={rule}
              onChange={(next) => updateRule(i, next)}
              onRemove={() => removeRule(i)}
              fieldPathOptions={fieldPathOptions}
              availableCategoryMasters={availableCategoryMasters}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** 保存前に rules 配列を検証する。各ルールの kind に必要な値が揃っているか確認。 */
export function validateRules(
  rules: AiValidatorRule[],
): { ok: true; value: AiValidatorsConfig | null } | { ok: false; error: string } {
  const cleaned: AiValidatorRule[] = [];
  for (let i = 0; i < rules.length; i += 1) {
    const r = rules[i]!;
    const fieldPath = r.fieldPath.trim();
    if (!fieldPath) {
      return { ok: false, error: `ルール ${i + 1}: fieldPath は必須です` };
    }
    if (r.kind === 'allowedValues') {
      if (!r.allowedValues || r.allowedValues.length === 0) {
        return { ok: false, error: `ルール ${i + 1}: allowedValues は 1 件以上指定してください` };
      }
      cleaned.push({ fieldPath, kind: 'allowedValues', allowedValues: [...r.allowedValues] });
    } else if (r.kind === 'maxItems') {
      if (typeof r.maxItems !== 'number' || !Number.isFinite(r.maxItems) || r.maxItems < 0) {
        return { ok: false, error: `ルール ${i + 1}: maxItems は 0 以上の数値で指定してください` };
      }
      cleaned.push({ fieldPath, kind: 'maxItems', maxItems: r.maxItems });
    } else if (r.kind === 'lengthRange') {
      if (
        !r.lengthRange ||
        !Number.isFinite(r.lengthRange[0]) ||
        !Number.isFinite(r.lengthRange[1])
      ) {
        return {
          ok: false,
          error: `ルール ${i + 1}: lengthRange は min と max 両方の数値が必要です`,
        };
      }
      cleaned.push({
        fieldPath,
        kind: 'lengthRange',
        lengthRange: [r.lengthRange[0], r.lengthRange[1]],
      });
    } else if (r.kind === 'categorySelection') {
      const categoryMasterId = r.categoryMasterId?.trim();
      if (!categoryMasterId) {
        return {
          ok: false,
          error: `ルール ${i + 1}: categoryMasterId は必須です (参照カテゴリマスタを選択してください)`,
        };
      }
      if (
        typeof r.selectionCount !== 'number' ||
        !Number.isFinite(r.selectionCount) ||
        r.selectionCount < 1
      ) {
        return {
          ok: false,
          error: `ルール ${i + 1}: selectionCount は 1 以上の数値で指定してください`,
        };
      }
      if (r.selectionCountMode !== 'max' && r.selectionCountMode !== 'exact') {
        return {
          ok: false,
          error: `ルール ${i + 1}: selectionCountMode は max / exact のいずれかを指定してください`,
        };
      }
      if (r.selectionOrderBy !== 'rank' && r.selectionOrderBy !== 'asListed') {
        return {
          ok: false,
          error: `ルール ${i + 1}: selectionOrderBy は rank / asListed のいずれかを指定してください`,
        };
      }
      const requireReason = r.requireReason === true;
      let reasonFieldPath: string | undefined;
      if (requireReason) {
        const v = r.reasonFieldPath?.trim();
        if (!v) {
          return {
            ok: false,
            error: `ルール ${i + 1}: requireReason=true のとき reasonFieldPath は必須です`,
          };
        }
        reasonFieldPath = v;
      }
      const cleanedRule: AiValidatorRule = {
        fieldPath,
        kind: 'categorySelection',
        categoryMasterId,
        selectionCount: r.selectionCount,
        selectionCountMode: r.selectionCountMode,
        selectionOrderBy: r.selectionOrderBy,
        requireReason,
      };
      if (reasonFieldPath) cleanedRule.reasonFieldPath = reasonFieldPath;
      cleaned.push(cleanedRule);
    }
  }
  if (cleaned.length === 0) return { ok: true, value: null };
  return { ok: true, value: { rules: cleaned } };
}

// ---------------------------------------------------------------------------
// 4. PromptInputMappingForm — テンプレ変数 ⇄ Post フィールドのマッピング
// ---------------------------------------------------------------------------

export function PromptInputMappingForm(props: {
  value: AiPromptInputMapping[];
  onChange: (next: AiPromptInputMapping[]) => void;
}) {
  const { value, onChange } = props;

  function addRow() {
    onChange([...value, { templateVar: '', source: '' }]);
  }

  function updateRow(i: number, patch: Partial<AiPromptInputMapping>) {
    const target = value[i];
    if (!target) return;
    const next = [...value];
    next[i] = { ...target, ...patch };
    onChange(next);
  }

  function removeRow(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          ※ AI システムプロンプト内 <code>{'{{<templateVar>}}'}</code> が{' '}
          <code>Post.&lt;source&gt;</code> の値で置換されます。source は <code>|</code> 区切りで
          fallback 指定可能 (例: <code>extraFields.a|extraFields.b</code>)。
        </p>
        <Button variant="secondary" size="sm" onClick={addRow}>
          ＋ マッピング行を追加
        </Button>
      </div>
      {value.length === 0 ? (
        <p className="text-xs text-slate-500">
          マッピングがありません。「＋ マッピング行を追加」で追加してください。
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-slate-600 border-b border-slate-200">
                <th className="py-1 pr-2 font-medium">プロンプト埋込変数名 (templateVar)</th>
                <th className="py-1 pr-2 font-medium">ソース (source、Post フィールドパス)</th>
                <th className="py-1 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {value.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1 pr-2">
                    <input
                      type="text"
                      value={row.templateVar}
                      onChange={(e) => updateRow(i, { templateVar: e.target.value })}
                      className={INPUT_CLASS}
                      placeholder="title"
                      aria-label={`マッピング ${i + 1} プロンプト埋込変数名 (templateVar)`}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      type="text"
                      value={row.source}
                      onChange={(e) => updateRow(i, { source: e.target.value })}
                      className={INPUT_CLASS}
                      placeholder="title または extraFields.foo"
                      aria-label={`マッピング ${i + 1} ソース (source、Post フィールドパス)`}
                    />
                  </td>
                  <td className="py-1 text-right">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeRow(i)}
                      aria-label={`マッピング ${i + 1} 削除`}
                    >
                      ✕
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
